/**
 * Company Delete Dialog Component - Confirm deletion
 */

import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  SupabaseCompany,
  useDeleteCompany 
} from '@/hooks/useSupabaseCompanies';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface CompanyDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: SupabaseCompany;
}

export const CompanyDeleteDialog: React.FC<CompanyDeleteDialogProps> = ({
  open,
  onOpenChange,
  company
}) => {
  const { toast } = useToast();
  const deleteCompany = useDeleteCompany();

  const handleDelete = async () => {
    try {
      await deleteCompany.mutateAsync(company.id);
      
      toast({
        title: "Thành công",
        description: `Đã xóa công ty "${company.company_name}"`,
      });
      
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Lỗi",
        description: error.message || "Lỗi khi xóa công ty",
        variant: "destructive",
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Xác nhận xóa công ty</AlertDialogTitle>
          <AlertDialogDescription>
            Bạn có chắc chắn muốn xóa công ty{' '}
            <span className="font-semibold">"{company.company_name}"</span>?
            <br />
            <br />
            <span className="text-muted-foreground text-sm">
              Lưu ý: Đây là soft delete, công ty sẽ được đánh dấu là không hoạt động 
              nhưng dữ liệu vẫn được giữ lại.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteCompany.isPending}>
            Hủy
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={deleteCompany.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteCompany.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Xóa công ty
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};